/**
 * Scope-aware access control aligned with Django RBAC direction.
 *
 * The backend uses ResourceFilter scopes: OWN, TEAM, PRACTICE, ALL, CUSTOM.
 * This module mirrors that on the frontend so UI gating is consistent.
 */

import type { Role } from '../../pages/users/types/rbac';

// ---------------------------------------------------------------------------
// Resource scope enum (matches Django ResourceFilter.scope)
// ---------------------------------------------------------------------------

export const RESOURCE_SCOPES = ['OWN', 'TEAM', 'PRACTICE', 'ALL', 'CUSTOM'] as const;
export type ResourceScope = (typeof RESOURCE_SCOPES)[number];

// ---------------------------------------------------------------------------
// Resource types for model-level capabilities
// ---------------------------------------------------------------------------

export type ResourceType =
  | 'appointment'
  | 'encounter'
  | 'episode_of_care'
  | 'patient'
  | 'care_team'
  | 'account'
  | 'coverage'
  | 'claim'
  | 'claim_response'
  | 'document_reference'
  | 'questionnaire_response'
  | 'communication'
  | 'task'
  | 'observation'
  | 'practitioner'
  | 'related_person'
  | 'group_session'
  | 'iop_program';

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

export type ResourceAction = 'view' | 'create' | 'edit' | 'delete' | 'sign' | 'cosign' | 'submit_claim';

// ---------------------------------------------------------------------------
// Role → scope defaults
// ---------------------------------------------------------------------------

interface ScopeDefaults {
  defaultScope: ResourceScope;
  viewScope: ResourceScope;
  editScope: ResourceScope;
}

const ROLE_SCOPE_DEFAULTS: Record<Role, ScopeDefaults> = {
  'unify-admin':      { defaultScope: 'ALL',      viewScope: 'ALL',      editScope: 'ALL' },
  'unify-assistant':  { defaultScope: 'ALL',      viewScope: 'ALL',      editScope: 'PRACTICE' },
  'practice-owner':   { defaultScope: 'PRACTICE', viewScope: 'PRACTICE', editScope: 'PRACTICE' },
  'practice-admin':   { defaultScope: 'PRACTICE', viewScope: 'PRACTICE', editScope: 'PRACTICE' },
  'practice-assistant': { defaultScope: 'PRACTICE', viewScope: 'PRACTICE', editScope: 'OWN' },
  supervisor:         { defaultScope: 'TEAM',     viewScope: 'TEAM',     editScope: 'TEAM' },
  provider:           { defaultScope: 'OWN',      viewScope: 'OWN',      editScope: 'OWN' },
  client:             { defaultScope: 'OWN',      viewScope: 'OWN',      editScope: 'OWN' },
};

export function getDefaultScope(role: Role): ResourceScope {
  return ROLE_SCOPE_DEFAULTS[role]?.defaultScope ?? 'OWN';
}

export function getViewScope(role: Role): ResourceScope {
  return ROLE_SCOPE_DEFAULTS[role]?.viewScope ?? 'OWN';
}

export function getEditScope(role: Role): ResourceScope {
  return ROLE_SCOPE_DEFAULTS[role]?.editScope ?? 'OWN';
}

// ---------------------------------------------------------------------------
// Scope hierarchy
// ---------------------------------------------------------------------------

const SCOPE_RANK: Record<ResourceScope, number> = {
  OWN: 0,
  TEAM: 1,
  PRACTICE: 2,
  ALL: 3,
  CUSTOM: -1,
};

export function scopeIncludes(userScope: ResourceScope, requiredScope: ResourceScope): boolean {
  if (userScope === 'CUSTOM' || requiredScope === 'CUSTOM') return false;
  return SCOPE_RANK[userScope] >= SCOPE_RANK[requiredScope];
}

// ---------------------------------------------------------------------------
// Workflow-specific permission checks
// ---------------------------------------------------------------------------

export interface WorkflowPermission {
  resource: ResourceType;
  action: ResourceAction;
  scope: ResourceScope;
}

const THERAPY_WORKFLOW_PERMISSIONS: Record<string, WorkflowPermission> = {
  'schedule_individual': { resource: 'appointment', action: 'create', scope: 'OWN' },
  'schedule_family': { resource: 'appointment', action: 'create', scope: 'OWN' },
  'schedule_group': { resource: 'group_session', action: 'create', scope: 'TEAM' },
  'schedule_iop': { resource: 'iop_program', action: 'create', scope: 'PRACTICE' },
  'mark_attendance': { resource: 'encounter', action: 'edit', scope: 'OWN' },
  'sign_note': { resource: 'document_reference', action: 'sign', scope: 'OWN' },
  'cosign_note': { resource: 'document_reference', action: 'cosign', scope: 'TEAM' },
  'submit_claim': { resource: 'claim', action: 'submit_claim', scope: 'OWN' },
  'view_claims': { resource: 'claim', action: 'view', scope: 'OWN' },
  'manage_care_team': { resource: 'care_team', action: 'edit', scope: 'PRACTICE' },
  'view_patient_chart': { resource: 'patient', action: 'view', scope: 'OWN' },
  'edit_patient_chart': { resource: 'patient', action: 'edit', scope: 'OWN' },
};

export function getWorkflowPermission(workflowKey: string): WorkflowPermission | undefined {
  return THERAPY_WORKFLOW_PERMISSIONS[workflowKey];
}

/**
 * Check if a role can perform a workflow action.
 * This is the primary entry point for UI gating checks.
 */
export function canPerformWorkflow(role: Role, workflowKey: string): boolean {
  const perm = THERAPY_WORKFLOW_PERMISSIONS[workflowKey];
  if (!perm) return false;
  const userScope = getDefaultScope(role);
  return scopeIncludes(userScope, perm.scope);
}

/**
 * Check if a role can perform an action on a resource at a given scope.
 */
export function canAccess(
  role: Role,
  _resource: ResourceType,
  action: ResourceAction,
  requiredScope: ResourceScope = 'OWN',
): boolean {
  const userScope = action === 'view' ? getViewScope(role) : getEditScope(role);
  return scopeIncludes(userScope, requiredScope);
}

// ---------------------------------------------------------------------------
// Ownership helpers
// ---------------------------------------------------------------------------

export type OwnershipRelation = 'self' | 'team_member' | 'practice_member' | 'unrelated';

export function getOwnershipRelation(opts: {
  resourceOwnerId: string;
  currentUserId: string;
  teamMemberIds: string[];
  practiceId?: string;
  resourcePracticeId?: string;
}): OwnershipRelation {
  if (opts.resourceOwnerId === opts.currentUserId) return 'self';
  if (opts.teamMemberIds.includes(opts.resourceOwnerId)) return 'team_member';
  if (opts.practiceId && opts.resourcePracticeId && opts.practiceId === opts.resourcePracticeId) {
    return 'practice_member';
  }
  return 'unrelated';
}

export function ownershipMatchesScope(
  relation: OwnershipRelation,
  scope: ResourceScope,
): boolean {
  switch (scope) {
    case 'ALL':
      return true;
    case 'PRACTICE':
      return relation !== 'unrelated';
    case 'TEAM':
      return relation === 'self' || relation === 'team_member';
    case 'OWN':
      return relation === 'self';
    case 'CUSTOM':
      return false;
  }
}
